import React, { useEffect, useMemo, useState, useCallback } from "react"; //
import useMailFetch from "../hooks/useMailFetch";
//import useMailAutoRefresh from "../hooks/useMailAutoRefresh";
import { AVAILABLE_COLUMNS } from "../constants/mailColumns";
import QueryForm from "../components/mail/QueryForm";
import ResultsTable from "../components/mail/ResultsTable"; 
import { useWorkspacesContext } from "../contexts/WorkspacesContext";
import "../App.css"; 

export default function DashboardPage() {
  
  const { activeWorkspace } = useWorkspacesContext();
  const { isLoading, isDataFetched, error, results, execute } = useMailFetch(activeWorkspace?.id);   

  const handleSearch = useCallback((criteria) => {
    // Prevent redundant searches if already loading or criteria are invalid/missing
    if (isLoading || !criteria || !criteria.folder || (criteria.startIndex === undefined || criteria.endIndex === undefined)) {
      console.warn("Search prevented: Invalid criteria or loading state.");
      return;
    }

    execute(criteria);
  }, [execute, isLoading]);

  /*
  const refreshEmails = useCallback(() => {
    // Prevent fetching if already loading or if lastCriteria is not set
    if (isLoading || !lastCriteria) {
      return;
    }
    execute(lastCriteria);
  }, [execute, lastCriteria, isLoading]);
*/
  // useMailAutoRefresh(refreshEmails, activeWorkspace?.id);

  // --- Columns Config ---
  const [columns, setColumns] = useState(AVAILABLE_COLUMNS);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const visibleColumns = useMemo(() => columns.filter((c) => c.isVisible), [columns]);

  const toggleColumnVisibility = (key) => {
    setColumns((prev) => prev.map((c) => (c.key === key ? { ...c, isVisible: !c.isVisible } : c)));
  };

  // --- Sort & Filter ---
  const [sortConfig, setSortConfig] = useState({ key: 'receivedDate', direction: 'descending' });
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState('all'); // all, today, yesterday, last3days, last7days
  const [filterAfterDate, setFilterAfterDate] = useState(''); // ISO string from datetime-local
  const [columnFilters, setColumnFilters] = useState({});

  useEffect(() => {
    setSortConfig({ key: 'receivedDate', direction: 'descending' });
    setSearchTerm('');
    setDateRange('all');
    setFilterAfterDate('');
    setColumnFilters({});
  }, [activeWorkspace?.id]); 

 const processedResults = useMemo(() => {
  if (!results || results.length === 0) return [];

  const data = results.map(item => {
    const auth = item.auth || {};
    
    return {
      ...item,
      spfStatus: (auth.spf || item.spfStatus || "UNKNOWN"),
      dkimStatus: String(auth.dkim || item.dkimStatus || "UNKNOWN"),
      dmarcStatus: (auth.dmarc || item.dmarcStatus || "UNKNOWN"),
      domain: item.domain || item.fromEmail?.split('@')[1] || "—" 
    };
  });

  return data;
}, [results]);

const filteredResults = useMemo(() => {
    let filtered = [...processedResults];

    // 1. Date Range Filter (Presets)
    if (dateRange !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.receivedDate || item.sentDate);
        if (isNaN(itemDate.getTime())) return true;
        
        const itemDay = new Date(itemDate.getFullYear(), itemDate.getMonth(), itemDate.getDate());
        
        if (dateRange === 'today') {
          return itemDay.getTime() === today.getTime();
        } else if (dateRange === 'yesterday') {
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          return itemDay.getTime() === yesterday.getTime();
        } else if (dateRange === 'last3days') {
          const threeDaysAgo = new Date(today);
          threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
          return itemDay >= threeDaysAgo;
        } else if (dateRange === 'last7days') {
          const sevenDaysAgo = new Date(today);
          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
          return itemDay >= sevenDaysAgo;
        }
        return true;
      });
    }

    // 2. Custom "Received After" Filter (Date + Time)
    if (filterAfterDate) {
      const afterDate = new Date(filterAfterDate);
      if (!isNaN(afterDate.getTime())) {
        filtered = filtered.filter(item => {
          const itemDate = new Date(item.receivedDate || item.sentDate);
          if (isNaN(itemDate.getTime())) return false;
          return itemDate >= afterDate;
        });
      }
    }

    // 3. Global Search (Search in all visible fields)
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        Object.entries(item).some(([key, value]) => {
          // Only search in fields that are likely visible or relevant
          if (['id', 'auth', 'metadata'].includes(key)) return false;
          return String(value).toLowerCase().includes(lowerSearch);
        })
      );
    }

    // 3. Column Specific Filters
    Object.entries(columnFilters).forEach(([key, value]) => {
      if (value) {
        const lowerVal = value.toLowerCase();
        filtered = filtered.filter(item => {
          const itemVal = String(item[key] || '').toLowerCase();
          return itemVal.includes(lowerVal);
        });
      }
    });

    return filtered;
  }, [processedResults, searchTerm, dateRange, filterAfterDate, columnFilters]);

const sortedResults = useMemo(() => {
    let sortableItems = [...filteredResults]; 
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        const valA = a[sortConfig.key];
        const valB = b[sortConfig.key];
        if (sortConfig.key === 'receivedDate' || sortConfig.key === 'sentDate') {
          const dateA = new Date(valA);
          const dateB = new Date(valB);
          if (dateA < dateB) return sortConfig.direction === 'ascending' ? -1 : 1;
          if (dateA > dateB) return sortConfig.direction === 'ascending' ? 1 : -1;
          return 0;
        }
        if (valA < valB) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [filteredResults, sortConfig]);

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  if (!activeWorkspace) {
    return (
      <div className="alert alert-info text-center mt-5" role="alert">
        Please select a workspace to start.
      </div>
    );
  }

  const displayName = activeWorkspace.accountName || activeWorkspace.name || activeWorkspace.email;

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div>
           <h1>Dashboard</h1>
           <div style={{color: 'var(--text-light)', fontSize: '14px'}}>
              Workspace: <strong>{displayName}</strong>
           </div>
        </div>
        <div className="user-badge" style={{background: 'var(--white)', padding: '8px 16px', borderRadius: '20px', boxShadow: 'var(--shadow)', border: '1px solid var(--border)'}}>
           <span style={{color: 'var(--success)', fontWeight: 'bold'}}>●</span> {activeWorkspace.email}
        </div>
      </header>

      <div className="filters-bar" style={{marginBottom: '20px'}}>
        <div className="row g-3">
          <div className="col-lg-9">
            <QueryForm     
              onSubmit={handleSearch} 
              isLoading={isLoading}
              workspace={activeWorkspace}   
            />
          </div>
          <div className="col-lg-3">
            <div className="card shadow-sm h-100 border-0 bg-white">
              <div className="card-header bg-white border-0 py-2 d-flex justify-content-between align-items-center">
                <span className="small fw-bold text-uppercase text-muted" style={{ fontSize: '10px', letterSpacing: '1px' }}>Active Filters</span>
                {(searchTerm || dateRange !== 'all' || filterAfterDate || Object.values(columnFilters).some(v => v)) && (
                  <button 
                    className="btn btn-link btn-sm p-0 text-danger text-decoration-none small" 
                    style={{ fontSize: '10px' }}
                    onClick={() => {
                      setSearchTerm('');
                      setDateRange('all');
                      setFilterAfterDate('');
                      setColumnFilters({});
                    }}
                  >
                    Clear All
                  </button>
                )}
              </div>
              <div className="card-body py-1 px-3 overflow-auto" style={{ maxHeight: '120px' }}>
                <div className="d-flex flex-wrap gap-1">
                  {/* Global Search */}
                  {searchTerm && (
                    <span className="badge bg-light text-dark border d-flex align-items-center gap-1 fw-normal" style={{ fontSize: '11px' }}>
                      Search: {searchTerm}
                      <span className="text-muted cursor-pointer" onClick={() => setSearchTerm('')}>×</span>
                    </span>
                  )}
                  
                  {/* Date Range Preset */}
                  {dateRange !== 'all' && (
                    <span className="badge bg-primary-soft text-primary border border-primary-subtle d-flex align-items-center gap-1 fw-normal" style={{ fontSize: '11px' }}>
                      Range: {dateRange}
                      <span className="cursor-pointer" onClick={() => setDateRange('all')}>×</span>
                    </span>
                  )}

                  {/* Filter After Date */}
                  {filterAfterDate && (
                    <span className="badge bg-success-soft text-success border border-success-subtle d-flex align-items-center gap-1 fw-normal" style={{ fontSize: '11px' }}>
                      After: {new Date(filterAfterDate).toLocaleString([], {month:'short', day:'numeric', hour:'2-digit', minute:'2-digit'})}
                      <span className="cursor-pointer" onClick={() => setFilterAfterDate('')}>×</span>
                    </span>
                  )}

                  {/* Column Filters */}
                  {Object.entries(columnFilters).map(([key, value]) => value && (
                    <span key={key} className="badge bg-info-soft text-info border border-info-subtle d-flex align-items-center gap-1 fw-normal" style={{ fontSize: '11px' }}>
                      {columns.find(c => c.key === key)?.name || key}: {value}
                      <span className="cursor-pointer" onClick={() => setColumnFilters(prev => ({...prev, [key]: ''}))}>×</span>
                    </span>
                  ))}

                  {/* No filters state */}
                  {!(searchTerm || dateRange !== 'all' || filterAfterDate || Object.values(columnFilters).some(v => v)) && (
                    <span className="text-muted small italic py-2">No active filters</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="table-wrapper">
        <ResultsTable
          columns={columns}
          visibleColumns={visibleColumns}
          results={sortedResults} // Use sortedResults instead of filteredResults since sorting is the final step
          isLoading={isLoading}
          isDataFetched={isDataFetched}
          error={error}
          onSort={handleSort}
          sortConfig={sortConfig}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          dateRange={dateRange}
          setDateRange={setDateRange}
          filterAfterDate={filterAfterDate}
          setFilterAfterDate={setFilterAfterDate}
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          onToggleColumn={toggleColumnVisibility}
          isSettingsOpen={isSettingsOpen}
          onToggleSettings={() => setIsSettingsOpen((v) => !v)}
        />
      </div>
      
      <div style={{padding: '16px', color: 'var(--text-light)', fontSize: '13px'}}>
         <span>Showing {filteredResults.length} results</span>
      </div>
    </div>
  );
}